import { EnterOutlined, PhoneOutlined } from '@ant-design/icons';
import type { ObserveFn } from '@hooks/use-intersection-observer';
import { useOnIntersect } from '@hooks/use-intersection-observer';
import useSessionStorage from '@hooks/use-session-storage';
import type {
  ApiMessage,
  Message,
  Message as MessageInterface,
  TelegramMenuMessageMetadata,
} from '@interfaces/message';
import useFocusMessageListElement from '@pages/ChatFeed/MiddleColumn/MessageList/MessageListContent/Message/hooks/use-focus-message-list-element';
import buildClassName from '@utils/build-class-name';
import { formatTime } from '@utils/date-format';
import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import DomPurify from 'dompurify';
import type { FC } from 'react';
import { useMemo, useRef } from 'react';

import { useMessageFocus, useSharedActions } from '@/newstore/selectors/shared';

import { SHOW_TELEGRAM_MENU_LABELS_KEY } from '../../../MiddleHeader';
import styles from './ServiceMessage.module.scss';

function getMessageHtmlId(messageId: number) {
  return `message${messageId.toString().replace('.', '-')}`;
}

interface OwnProps {
  message: Message;
  observeIntersectionForBottom: ObserveFn;
}

const INTELLIGENT_SUPPORT_BOT_SENDER = 'Intelligent Support Bot';

function getTelegramMenuMetadata(message: Message): TelegramMenuMessageMetadata | undefined {
  if (!message.extra_attributes || Array.isArray(message.extra_attributes)) {
    return undefined;
  }

  return message.extra_attributes.telegram_menu;
}

function getTelegramMenuDisplayText(message: Message) {
  const telegramMenu = getTelegramMenuMetadata(message);
  if (!telegramMenu) {
    return undefined;
  }

  return telegramMenu.subkeyboard_label || telegramMenu.label;
}

function stripFormattingTags(text: string) {
  return new DOMParser().parseFromString(text, 'text/html').body.textContent ?? text;
}

const ServiceMessage: FC<OwnProps> = ({ message, observeIntersectionForBottom }) => {
  const [showTelegramMenuLabels] = useSessionStorage(SHOW_TELEGRAM_MENU_LABELS_KEY, false);
  const { openForwardMenu } = useSharedActions();

  const messageDate = useMemo(() => new Date(message.created_at), [message.created_at]);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomMarkerRef = useRef<HTMLDivElement>(null);
  useOnIntersect(bottomMarkerRef, observeIntersectionForBottom);
  const messageId = (message.id || message.localId) as number;
  const focus = useMessageFocus(message);
  useFocusMessageListElement({
    elementRef: containerRef,
    isFocused: Boolean(focus),
    focusVersion: focus?.focusVersion,
    scrollTargetPosition: focus?.scrollTargetPosition,
  });

  const canBeForwarded = (message: MessageInterface): message is ApiMessage & { id: number; conversation_id: number } =>
    message.id !== undefined && message.conversation_id !== undefined && !message.deleted_at;

  const telegramMenu = getTelegramMenuMetadata(message);
  const telegramMenuDisplayText = getTelegramMenuDisplayText(message);
  const telegramReply =
    telegramMenu?.subkeyboard_label || !telegramMenu?.telegram_reply ? undefined : telegramMenu.telegram_reply;
  const shouldShowTelegramMenuLabel = showTelegramMenuLabels && telegramMenuDisplayText !== undefined;
  const isBotSubkeyboardMessage = Boolean(
    shouldShowTelegramMenuLabel && telegramMenu?.subkeyboard_label && !message.telegram_user_id,
  );
  const telegramMenuSender = isBotSubkeyboardMessage ? INTELLIGENT_SUPPORT_BOT_SENDER : message.author_name;
  const containerClassName = buildClassName(
    'message-list-item',
    styles.actionMessage,
    shouldShowTelegramMenuLabel && styles.telegramMenuMessage,
  );
  const messageBody = shouldShowTelegramMenuLabel ? stripFormattingTags(telegramMenuDisplayText) : message.body;

  const contextMenuItems: MenuProps['items'] = [
    ...(canBeForwarded(message)
      ? [
          {
            key: 'forward',
            label: 'Forward',
            icon: <EnterOutlined style={{ transform: 'scale(-1, -1)' }} />,
          },
        ]
      : []),
  ];

  const onContextMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (key === 'forward' && canBeForwarded(message)) {
      openForwardMenu(message.conversation_id, message.id);
    }
  };

  return (
    <div ref={containerRef} id={getMessageHtmlId(messageId)} className={containerClassName} data-message-id={messageId}>
      <div ref={bottomMarkerRef} className="bottom-marker" data-message-id={messageId} />
      {message.author_name && !shouldShowTelegramMenuLabel && (
        <div className={styles.messageTitle} dir="ltr">
          Service message triggered by {message.author_name} at {formatTime(messageDate)}
          {message.author_phone && (
            <a
              className={styles.messageTitlePhone}
              href={`tel:${message.author_phone}`}
              title={`Call ${message.author_name}`}
            >
              <PhoneOutlined />
            </a>
          )}
        </div>
      )}
      <Dropdown
        trigger={['contextMenu']}
        menu={{
          items: contextMenuItems,
          onClick: onContextMenuClick,
        }}
      >
        {shouldShowTelegramMenuLabel ? (
          <div className={styles.telegramMenuMessages}>
            <div className={buildClassName(styles.messageContent, isBotSubkeyboardMessage && styles.telegramMenuReply)}>
              {telegramMenuSender && (
                <div className={styles.telegramMenuSender} dir="ltr">
                  {telegramMenuSender}
                  {!isBotSubkeyboardMessage && message.author_phone && (
                    <a
                      className={styles.messageTitlePhone}
                      href={`tel:${message.author_phone}`}
                      title={`Call ${message.author_name}`}
                    >
                      <PhoneOutlined />
                    </a>
                  )}
                </div>
              )}
              <div className={styles.telegramMenuText}>{messageBody}</div>
              <div className={styles.telegramMenuMeta} dir="ltr">
                {formatTime(messageDate)}
              </div>
            </div>
            {telegramReply && (
              <div className={styles.telegramMenuReply}>
                <div className={styles.telegramMenuSender} dir="ltr">
                  {INTELLIGENT_SUPPORT_BOT_SENDER}
                </div>
                <div
                  className={styles.telegramMenuText}
                  dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(telegramReply) }}
                />
                <div className={styles.telegramMenuMeta} dir="ltr">
                  {formatTime(messageDate)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.messageContent}>{messageBody}</div>
        )}
      </Dropdown>
    </div>
  );
};

export default ServiceMessage;
